/**
 * Reward charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_classroom`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const rewardChartsPack: ToilePack = {
    slug: 'reward-charts',
    category: 'classroom',
    name: 'Reward charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'grid', label: 'Ten squares', subject: 'a grid of ten big empty sticker squares under the words "MY REWARD CHART", a small gold trophy waiting after the last square'},
        {key: 'path', label: 'Treasure path', subject: 'ten round stepping stones winding up toward an open treasure chest, each stone left empty for a sticker, the words "MY TREASURE HUNT" across the top'},
        {key: 'rocket', label: 'Rocket climb', subject: 'a rocket climbing a column of ten empty circles toward a ringed planet, the words "BLAST OFF" across the top'},
    ],
    styles: [
        {key: 'crayon', label: 'Crayon', style: 'bright kindergarten poster art drawn with a thick marker line and simple flat fills', palette: 'primary red, yellow, green and blue on white', framing: 'The chart filling the page with a wide even margin all round.'},
        {key: 'pastel', label: 'Pastel', style: 'soft nursery illustration, gentle watercolor washes over a light pencil outline', palette: 'blush pink, mint and pale sky blue on cream paper', framing: 'The chart centered on the page with a generous quiet margin.'},
        {key: 'clean', label: 'Clean', style: 'flat modern classroom design, thin even outlines and crisp geometric shapes', palette: 'black and white with one strong marigold accent', framing: 'The chart set square on a plain white page, margins even on all four sides.'},
    ],
    directions: ['for chores at home', 'with twenty squares', 'as a birthday countdown'],
    seo: {
        title: 'Reward chart printable: sticker chart in 3 styles',
        metaDescription: 'Nine reward charts — ten squares, treasure path and rocket climb — in crayon, pastel and clean styles, one sticker per square.',
        h1: 'Reward chart: ten squares, treasure path and rocket climb in three styles',
        tagline: 'Nine reward charts, three layouts, three looks — one sticker per square, ten to a prize.',
        intro: 'The grid on the wall or the fridge that a sticker fills one square at a time. These nine print in three layouts — a ten-square grid, a path of stepping stones to a treasure chest, a rocket climbing toward a planet — each in three looks, from crayon-bright to clean and calm. Type a different prize or theme and the chart redraws the same way.',
        useCases: [
            'a classroom sticker chart',
            'a fridge behavior chart at home',
            'a reading-log reward tracker',
            'a chore chart for kids',
        ],
        faq: [
            {q: 'How many squares does each chart have?', a: 'The ten-square and stepping-stone layouts hold ten stickers to a prize; the rocket layout climbs through the same number of stages.'},
            {q: 'Can I change the prize or theme?', a: 'Yes — type a different destination or theme and the chart redraws in the same three styles.'},
            {q: 'What size does it print at?', a: 'Portrait A4 proportions (832 by 1216), sized to print clean on standard paper for a wall or fridge.'},
        ],
    },
}
